import { defineStore } from 'pinia';
import { FormContext } from 'vee-validate';

import {
  GiftBalanceAdjustmentSalesManagementResponse,
  PageVoid,
  WalletUserGiftInfo,
  WalletUserRepository,
} from '@/repositories/generated';
import {
  BalanceTypeKey,
  BalanceTypeKeys,
  WalletUserBalanceDetailType,
  balanceUnit,
} from '@/types';
import {
  giftBalanceAdjustSchema,
  useForm,
} from '@/validations';

type WalletUserBalanceDetailParam = {
  walletUserId: string;
  paymentMethodId: number;
  walletServiceId: string;
};

type BalanceDetailUiType = {
  valueTotal: number;
  pointTotal: number;
  typeDetail: Record<BalanceTypeKey, WalletUserBalanceDetailType>;
};

type BalanceDetailCtx = {
  param: WalletUserBalanceDetailParam | undefined;
  data: BalanceDetailUiType | undefined;
  lastUpdatedAt: string | undefined;
  loading: boolean;
  walletService: {
    pointRatio: number;
  };
  salesManagements: GiftBalanceAdjustmentSalesManagementResponse[];
};

type UiForm = {
  differenceBalance: number | undefined;
  isDeposit: boolean;
  reason: string | undefined;
  giftSalesManagementId: number | undefined;
};

const EmptyError: Record<keyof UiForm, string> = {
  differenceBalance: '',
  isDeposit: '',
  reason: '',
  giftSalesManagementId: '',
};

type State = {
  form: Record<BalanceTypeKey, FormContext<UiForm, UiForm>>;
  initData: BalanceDetailCtx;
};

const initialForm = () => useForm<UiForm>({
  validationSchema: giftBalanceAdjustSchema,
  initialValues: {
    differenceBalance: undefined,
    isDeposit: true,
    reason: undefined,
    giftSalesManagementId: undefined,
  },
});

export const useGiftBalanceAdjustStore = defineStore('giftBalanceAdjust', {
  state: (): State => ({
    form: {
      PREPAID_VALUE: initialForm(),
      FUNDS_TRANSFER_VALUE: initialForm(),
      BONUS_VALUE: initialForm(),
      BASIC_POINT: initialForm(),
      BONUS_POINT: initialForm(),
    },
    initData: {
      param: undefined,
      data: undefined,
      lastUpdatedAt: undefined,
      loading: false,
      walletService: {
        pointRatio: 0,
      },
      salesManagements: [],
    },
  }),
  actions: {
    /**
     * initializer
     */
    initializeForm(type: BalanceTypeKey) {
      this.form[type].setErrors(EmptyError);
    },
    async initializeData(
      walletUserId: string,
      giftInfo: WalletUserGiftInfo,
      pointRatio: number,
    ) {
      this.$reset();

      const param = {
        walletUserId,
        paymentMethodId: giftInfo.giftPaymentMethodId,
        walletServiceId: giftInfo.walletServiceId,
      };
      this.initData.param = param;
      this.initData.loading = true;

      try {
        const [
          giftBalanceDetail,
          salesManagements,
        ] = await Promise.all([
          WalletUserRepository.balance1({
            walletUserId: param.walletUserId,
            paymentMethodId: param.paymentMethodId.toString(),
            walletServiceId: param.walletServiceId,
          }),
          WalletUserRepository.index4({
            walletUserId: param.walletUserId,
            paymentMethodId: param.paymentMethodId,
          }),
        ]);

        // 商品券は前払バリュー / ボーナスバリューのみ。資金移動バリュー・ポイントは対象外
        const emptyDetail: WalletUserBalanceDetailType = { balanceTotal: 0 };
        this.initData.data = {
          valueTotal: giftBalanceDetail.prepaidValueTotal + giftBalanceDetail.bonusValueTotal,
          pointTotal: 0,
          typeDetail: {
            PREPAID_VALUE: {
              balanceTotal: giftBalanceDetail.prepaidValueTotal,
              balanceExpiresAt: giftBalanceDetail.prepaidValueExpiresAt,
            },
            FUNDS_TRANSFER_VALUE: emptyDetail,
            BONUS_VALUE: {
              balanceTotal: giftBalanceDetail.bonusValueTotal,
              balanceExpiresAt: giftBalanceDetail.bonusValueExpiresAt,
            },
            BASIC_POINT: emptyDetail,
            BONUS_POINT: emptyDetail,
          },
        };
        this.initData.lastUpdatedAt = giftBalanceDetail.lastUpdatedAt;
        this.initData.walletService = {
          pointRatio,
        };
        this.initData.salesManagements = salesManagements;
      } catch {
        throw Error('残高情報の取得に失敗しました。');
      } finally {
        this.initData.loading = false;
      }
    },
    /**
     * validation, request
     */
    async validateForm(type: BalanceTypeKey): Promise<boolean> {
      const form = this.form[type];
      await form.validate();
      // Yupでチェックできない項目をここでチェックする
      if (!form.errors.differenceBalance && !form.values.isDeposit && this.getAdjustedBalance(type) < 0) {
        form.setFieldError('differenceBalance', '残高は0未満に減算できません。');
      }
      if (!form.errors.differenceBalance && !form.values.isDeposit && this.getAdjustedTotalBalance(type) < 0) {
        form.setFieldError('differenceBalance', '合計残高は0未満に減算できません。');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isEmpty = (it: any) => Object.keys(it).length === 0;
      return isEmpty(form.errors);
    },
    async adjustBalance(type: BalanceTypeKey): Promise<PageVoid> {
      const {
        param,
        lastUpdatedAt,
      } = this.initData;

      if (param == null || lastUpdatedAt == null || this.form == null) {
        return Promise.reject();
      }
      const form = this.form[type].values;

      return WalletUserRepository.index3({
        walletUserId: param.walletUserId,
        requestBody: {
          adjustedBalance: this.getAdjustedBalance(type),
          balanceType: type,
          isDeposit: form.isDeposit,
          giftSalesManagementId: form.isDeposit ? form.giftSalesManagementId : undefined,
          lastUpdatedAt,
          paymentMethodId: param.paymentMethodId,
          reason: form.reason ?? '',
        },
      });
    },
    // 調整後残高
    getAdjustedBalance(type: BalanceTypeKey): number {
      if (this.initData.data == null) {
        return 0;
      }
      const { balanceTotal } = this.initData.data.typeDetail[type];
      const form = this.form[type].values;
      const diff = form.differenceBalance ?? 0;
      return form.isDeposit ? balanceTotal + diff : balanceTotal - diff;
    },
    // 調整後プリペイド合計残高 (ポイント比率を考慮)
    getAdjustedTotalBalance(type: BalanceTypeKey): number {
      if (this.initData.data == null) {
        return 0;
      }
      const multiplePointRatio = (balanceType: BalanceTypeKey, value: number) => (
        balanceUnit[balanceType] === 'pt'
          ? value * this.initData.walletService.pointRatio
          : value
      );
      const total = BalanceTypeKeys
        .map((it) => multiplePointRatio(it, this.initData.data?.typeDetail[it].balanceTotal ?? 0))
        .reduce((a, b) => a + b);

      const form = this.form[type].values;
      const diff = multiplePointRatio(type, form.differenceBalance ?? 0);
      return form.isDeposit ? total + diff : total - diff;
    },
    // 選択中の販売管理 (加算プレビュー用)
    getSelectedSalesManagement(type: BalanceTypeKey): GiftBalanceAdjustmentSalesManagementResponse | undefined {
      const id = this.form[type].values.giftSalesManagementId;
      if (id == null) {
        return undefined;
      }
      return this.initData.salesManagements.find((it) => it.giftSalesManagementId === id);
    },
  },
});
